import { createFileRoute, redirect, notFound } from "@tanstack/react-router";
import LearnArticleRoute, {
  findLearnPageBySlug,
} from "@/pages/learn/LearnArticleRoute";

/**
 * Learn slugs whose page data canonicals point to a different URL.
 * 301-redirect these to their canonical target instead of rendering a
 * page with a cross-canonical (which would split/duplicate content).
 */
const LEARN_SLUG_REDIRECTS: Record<string, string> = {
  "saint-tms-boca-raton": "/locations/saint-tms-boca-raton",
  "saint-tms-florida": "/saint-tms-florida",
  "saint-tms-fort-lauderdale": "/locations/saint-tms-fort-lauderdale",
  "saint-tms-miami": "/locations/saint-tms-miami",
  "blue-cross-blue-shield-saint-tms":
    "/insurance/blue-cross-blue-shield-saint-tms",
  "saint-tms-cost-hub": "/learn/saint-tms-cost",
  "saint-tms-cost-insurance-coverage": "/learn/saint-tms-cost",
  // Consolidated into /learn/saint-tms-locations
  "where-can-i-get-saint-tms": "/learn/saint-tms-locations",
  "saint-tms-near-me": "/learn/saint-tms-locations",
  "is-saint-available-near-me": "/learn/saint-tms-locations",
  "where-can-i-get-saint-treatment-for-depression":
    "/learn/saint-tms-locations",
  "how-to-find-a-saint-tms-provider": "/learn/saint-tms-locations",
  "saint-tms-east-coast": "/learn/saint-tms-locations",
  "5-day-tms-treatment": "/learn/saint-tms-locations",
  "saint-tms-coral-springs-south-florida": "/learn/saint-tms-locations",
  // Consolidated into /learn/is-saint-tms-safe
  "what-are-the-side-effects-of-saint-tms": "/learn/is-saint-tms-safe",
  // Consolidated into /learn/saint-vs-spravato (Spravato merge)
  "tms-vs-spravato": "/learn/saint-vs-spravato",
  // Consolidated into /learn/travel-for-saint-tms
  "can-someone-travel-with-me-for-saint-tms": "/learn/travel-for-saint-tms",
  // Consolidated into /what-is-accelerated-tms
  "accelerated-tms-vs-conventional-tms": "/what-is-accelerated-tms",
  // Consolidated into /learn/what-is-saint-tms
  "what-is-saint-treatment-for-depression": "/learn/what-is-saint-tms",
  "stanford-accelerated-intelligent-neuromodulation-therapy":
    "/learn/what-is-saint-tms",
  // Legacy short slug — redirect, never 404
  "saint-tms": "/learn/what-is-saint-tms",
  // Consolidated into /learn/saint-vs-traditional-tms
  "saint-vs-traditional-tms": "/learn/saint-vs-traditional-tms",
  // Consolidated into /learn/how-fast-does-saint-tms-work
  "how-fast-does-saint-tms-work-compared-to-tms":
    "/learn/how-fast-does-saint-tms-work",
  // Consolidated into /learn/what-happens-between-saint-sessions
  "what-to-expect-during-saint-tms":
    "/learn/what-happens-between-saint-sessions",
  "how-long-does-saint-tms-treatment-take":
    "/learn/what-happens-between-saint-sessions",
  "saint-treatment-week-day-by-day":
    "/learn/what-happens-between-saint-sessions",
  // Consolidated into /learn/fmri-guided-tms-for-depression
  // (accelerated-tms-fmri-targeting and what-is-personalized-tms-targeting
  // are non-learn URLs handled in _redirects)
  // Consolidated into /learn/does-saint-tms-require-anesthesia
  "does-saint-tms-require-anesthesia-or-medication":
    "/learn/does-saint-tms-require-anesthesia",
  // Consolidated into /learn/can-i-drive-and-work-during-saint-tms
  "can-i-work-during-saint-tms-treatment-week":
    "/learn/can-i-drive-and-work-during-saint-tms",
  // Consolidated into /learn/outpatient-saint-tms
  "is-saint-tms-outpatient-or-hospital": "/learn/outpatient-saint-tms",
  // Consolidated into /learn/does-insurance-cover-saint-tms
  "is-saint-tms-covered-by-insurance": "/learn/does-insurance-cover-saint-tms",
  // Consolidated into /learn/is-saint-for-treatment-resistant-depression
  // (saint-for-treatment-resistant-depression is non-learn, in _redirects)
  // Consolidated into /learn/tms-for-anxiety-ocd-ptsd
  // (accelerated-tms-other-conditions is non-learn, in _redirects)
  // Consolidated into /learn/fast-depression-treatment
  "fastest-treatment-for-severe-depression": "/learn/fast-depression-treatment",
  "fast-treatment-resistant-depression-options":
    "/learn/fast-depression-treatment",
  // Consolidated into /learn/what-is-tms-therapy-for-depression
  // (what-is-tms-therapy and /contents/tms/what-is-tms-therapy are non-learn)
  // Consolidated into /learn/when-antidepressants-dont-work
  "what-happens-when-two-antidepressants-dont-work":
    "/learn/when-antidepressants-dont-work",
  "why-still-depressed-while-taking-medication":
    "/learn/when-antidepressants-dont-work",
  "depression-wont-go-away-even-with-medication":
    "/learn/when-antidepressants-dont-work",
  // Consolidated into /learn/tms-after-antidepressants-fail
  "can-tms-help-after-antidepressants-fail":
    "/learn/tms-after-antidepressants-fail",
  // Consolidated into /learn/can-saint-help-if-antidepressants-didnt-work
  "saint-after-medication-failure":
    "/learn/can-saint-help-if-antidepressants-didnt-work",
  // Consolidated into /learn/depression-treatment-without-medication
  "depression-treatment-without-more-medication":
    "/learn/depression-treatment-without-medication",
  "depression-treatment-without-another-medication":
    "/learn/depression-treatment-without-medication",
  "dont-want-to-keep-changing-antidepressants":
    "/learn/depression-treatment-without-medication",
  // Consolidated into /depression-treatment-not-working
  "nothing-works-for-my-depression": "/depression-treatment-not-working",
  "what-options-are-left-when-nothing-works-for-depression":
    "/depression-treatment-not-working",
  "depression-feels-impossible-to-treat": "/depression-treatment-not-working",
  "tired-of-feeling-depressed-options-left":
    "/depression-treatment-not-working",
  "tired-of-feeling-depressed-options": "/depression-treatment-not-working",
  "depressed-for-years-what-else-can-i-try":
    "/depression-treatment-not-working",
  "lost-hope-after-depression-treatments": "/depression-treatment-not-working",
  "what-comes-after-medication-and-therapy-for-depression":
    "/depression-treatment-not-working",
  // Consolidated into /learn/why-does-my-depression-keep-coming-back
  "why-does-depression-keep-coming-back":
    "/learn/why-does-my-depression-keep-coming-back",
  // Consolidated into /learn/options-if-therapy-doesnt-help-depression
  "what-if-therapy-isnt-helping-depression":
    "/learn/options-if-therapy-doesnt-help-depression",
  // Consolidated into /learn/tms-vs-medication
  "tms-vs-medication-for-depression": "/learn/tms-vs-medication",
  // Consolidated into /learn/what-is-a-verified-saint-provider
  "questions-to-ask-a-saint-provider":
    "/learn/what-is-a-verified-saint-provider",
  "how-to-find-a-legitimate-saint-provider":
    "/learn/what-is-a-verified-saint-provider",
};

export const Route = createFileRoute("/learn/$slug")({
  beforeLoad: ({ params }) => {
    const target = LEARN_SLUG_REDIRECTS[params.slug];
    if (target) {
      throw redirect({ to: target, statusCode: 301 });
    }
    if (!findLearnPageBySlug(params.slug)) {
      throw notFound();
    }
  },
  component: RouteComponent,
  head({ params }) {
    const page = findLearnPageBySlug(params.slug);
    if (!page) {
      return {
        meta: [{ title: "Not found" }, { name: "robots", content: "noindex" }],
      };
    }
    const url = `https://neurowellnesstms.com${page.canonical}`;
    // Strip any existing brand suffix from the page title so we never
    // produce a doubled "… | Neuro Wellness | Neuro Wellness".
    const cleanTitle = page.title
      .replace(/\s*\|\s*Neuro Wellness.*$/i, "")
      .trim();
    const title = `${cleanTitle} | Neuro Wellness`;
    return {
      meta: [
        { title },
        { name: "description", content: page.description },
        { property: "og:title", content: title },
        { property: "og:description", content: page.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(page.schemaJson),
        },
      ],
    };
  },
});

function RouteComponent() {
  return <LearnArticleRoute />;
}
